import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type { LayoutStore, LayoutState, ModalConfig } from './types';
import { checkDevEnv } from '../utils/env';
import { createSingleKeySetter, type SingleKeySetter } from '@stores/utils';

const initialState: LayoutState = {
    isGlobalLoading: false,
    loadingMessage: null,
    breadcrumbs: [],
    modals: {},
    logoOrgId: undefined,
};

const PREFIX = 'layout';

// NOTE: Sidebar/navbar collapsed state is managed by the layout store
// shipped with @dynamon-as/ui (`useLayoutStore` from the package).

export const useLayoutStore = create<LayoutStore>()(
    devtools(
        (set) => {
            const createLayoutSetter = (
                key: keyof LayoutState,
            ): SingleKeySetter<LayoutStore, keyof LayoutState> =>
                createSingleKeySetter(set, key, PREFIX);

            return {
                ...initialState,

                // Loading
                setGlobalIsLoading: (isLoading, message = null): void =>
                    set(
                        {
                            isGlobalLoading: isLoading,
                            loadingMessage: message,
                        },
                        undefined,
                        `${PREFIX}/setGlobalIsLoading`,
                    ),

                // Breadcrumbs
                setBreadcrumbs: createLayoutSetter('breadcrumbs'),

                addBreadcrumb: (breadcrumb): void =>
                    set(
                        (state) => ({
                            breadcrumbs: [...state.breadcrumbs, breadcrumb],
                        }),
                        undefined,
                        `${PREFIX}/addBreadcrumb`,
                    ),

                clearBreadcrumbs: (): void =>
                    set(
                        { breadcrumbs: [] },
                        undefined,
                        `${PREFIX}/clearBreadcrumbs`,
                    ),

                // Modals
                openModal: (id, config = {}): void =>
                    set(
                        (state) => ({
                            modals: {
                                ...state.modals,
                                [id]: { id, isOpen: true, ...config },
                            },
                        }),
                        undefined,
                        `${PREFIX}/openModal:${id}`,
                    ),

                closeModal: (id): void =>
                    set(
                        (state) => {
                            const modal = state.modals[id];
                            if (!modal) {
                                return state;
                            }
                            const updatedModals: Record<string, ModalConfig> = {
                                ...state.modals,
                                [id]: { ...modal, isOpen: false },
                            };
                            return { modals: updatedModals };
                        },
                        undefined,
                        `${PREFIX}/closeModal:${id}`,
                    ),

                closeAllModals: (): void =>
                    set(
                        (state) => {
                            const updatedModals: Record<string, ModalConfig> =
                                {};
                            for (const [key, modal] of Object.entries(
                                state.modals,
                            )) {
                                updatedModals[key] = {
                                    ...modal,
                                    isOpen: false,
                                };
                            }
                            return { modals: updatedModals };
                        },
                        undefined,
                        `${PREFIX}/closeAllModals`,
                    ),

                // Logo
                setLogoOrgId: createLayoutSetter('logoOrgId'),
            };
        },
        {
            name: 'LayoutStore',
            enabled: checkDevEnv(),
            store: 'LayoutStore',
        },
    ),
);
